import { dateLocale } from "@/lib/dateTime";
import React from "react";
import { formatINR } from "@/lib/utils";
import type { ProposalRender, ProposalStyle } from "@/types/proposal";

const fmtDate = (d?: string | null) =>
  d ? new Date(d.length === 10 ? `${d}T00:00:00` : d).toLocaleDateString(dateLocale(), { day: "2-digit", month: "short", year: "numeric" }) : "-";

/** On screen, the design's fonts (with close fallbacks when they aren't installed) */
const FONT_STACK: Record<ProposalStyle["font"], string> = {
  sans: 'Roboto, "Segoe UI", Arial, sans-serif',
  serif: '"Noto Serif", Georgia, "Times New Roman", serif',
  modern: 'Poppins, Montserrat, "Segoe UI", sans-serif',
};
/** A colour mixed with white (0 = the colour, 1 = white), for the light strip behind titles */
const tint = (hex: string, amount: number) => {
  const n = parseInt(hex.slice(1), 16);
  const mix = (c: number) => Math.round(c + (255 - c) * amount);
  return `rgb(${mix(n >> 16)}, ${mix((n >> 8) & 255)}, ${mix(n & 255)})`;
};
/** How a section title looks in a design */
const titleStyle = (st: ProposalStyle): React.CSSProperties => ({
  color: st.colors.heading,
  fontSize: `${st.sizes.heading + 2}px`,
  ...(st.heading === "underline" ? { borderBottom: `1px solid ${st.colors.accent}`, paddingBottom: 4 } : {}),
  ...(st.heading === "band" ? { background: tint(st.colors.accent, 0.88), padding: "4px 8px", borderRadius: 2 } : {}),
  ...(st.heading === "left_bar" ? { borderLeft: `3px solid ${st.colors.accent}`, paddingLeft: 8 } : {}),
});

export interface LetterheadView {
  src: string;
  /** Room the artwork takes at the top and bottom of page 1, in millimetres of an A4 page */
  top: number;
  bottom: number;
}

/**
 * A proposal laid out as the customer reads it. Section HTML comes from /Proposal/Preview, where it
 * is sanitized on the server and the merged values are escaped, so it is rendered as HTML here.
 */
export function ProposalDocument({ doc, logoSrc = "/api/Company/Logo", letterhead }: { doc: ProposalRender; logoSrc?: string; letterhead?: LetterheadView | null }) {
  // On the company letterhead: the top and bottom of its first page frame the document, and the logo and address (which it carries) are left out
  const lh: LetterheadView | null = letterhead ?? (doc.letterhead ? { src: `/api/Proposal/Letterhead/${doc.letterhead.id}`, top: doc.letterhead.top, bottom: doc.letterhead.bottom } : null);
  // The logo is optional: if the image can't be loaded (none uploaded), the company name is shown instead
  const [logoFailed, setLogoFailed] = React.useState(false);
  const logo = lh || logoFailed || !logoSrc ? null : (
    <img src={logoSrc} alt={doc.company.name} onError={() => setLogoFailed(true)} className="h-16 max-w-[220px] object-contain object-left" />
  );
  const cover = doc.sections.find((s) => s.type === "cover");
  const hasCover = !!cover && cover.variant !== "compact";
  const compact = !!cover && cover.variant === "compact";
  // A template's design (fonts, colours, title and table style); without one, the standard look
  const st = doc.style || null;
  const c = st?.colors;
  const centered = st?.cover.layout === "centered";
  let n = 0;
  return (
    <article
      className="bg-white text-foreground rounded-xl shadow-sm border max-w-3xl mx-auto text-sm leading-relaxed overflow-hidden shrink-0"
      style={st ? { fontFamily: FONT_STACK[st.font], color: c!.text, fontSize: `${st.sizes.base + 4}px` } : undefined}
    >
      {lh && lh.top > 0 && <div aria-hidden style={{ aspectRatio: `210 / ${lh.top}`, backgroundImage: `url(${lh.src})`, backgroundSize: "100% auto", backgroundPosition: "top", backgroundRepeat: "no-repeat" }} />}
      {hasCover ? (
        <header className={`px-8 sm:px-12 pt-10 pb-10 border-b ${centered ? "text-center" : ""}`} data-section-id={doc.sections.find((x) => x.type === "cover")?.id}>
          {!lh && (
            <>
              {logo ? <div className={centered ? "flex justify-center" : ""}>{logo}</div> : <div className="text-base font-bold text-primary" style={c ? { color: c.accent } : undefined}>{doc.company.name}</div>}
              <div className="text-[11px] text-muted-foreground">
                {[doc.company.address, doc.company.phone, doc.company.email, doc.company.website].filter(Boolean).join("  ·  ")}
              </div>
            </>
          )}
          <div className={lh ? "h-0.5 bg-primary mb-5" : "h-0.5 bg-primary mt-12 mb-5"} style={c ? { background: c.accent } : undefined} />
          <div className="text-[11px] font-bold tracking-[0.25em] text-muted-foreground" style={c ? { color: c.muted } : undefined}>{(st?.cover.subtitle || "Proposal").toUpperCase()}</div>
          <h1 className="text-2xl sm:text-3xl font-bold mt-1 mb-6 text-foreground" style={st ? { color: c!.heading === c!.accent ? c!.text : c!.heading, fontSize: `${st.sizes.cover_title + 4}px`, lineHeight: 1.2 } : undefined}>{doc.title}</h1>
          <div className="text-xs text-muted-foreground">Prepared for</div>
          <div className="text-lg font-bold text-foreground">{doc.customer.company || "-"}</div>
          {doc.customer.contact && <div>{[doc.customer.contact, doc.customer.job_title].filter(Boolean).join(", ")}</div>}
          <div className="grid grid-cols-3 gap-4 mt-8 text-xs">
            <div>
              <div className="text-muted-foreground">Proposal no.</div>
              <div className="font-semibold font-mono">{doc.display_number}</div>
            </div>
            <div>
              <div className="text-muted-foreground">Date</div>
              <div className="font-semibold">{fmtDate(doc.date)}</div>
            </div>
            <div>
              <div className="text-muted-foreground">Valid until</div>
              <div className="font-semibold">{fmtDate(doc.valid_until)}</div>
            </div>
          </div>
          <div className="mt-6 text-xs">
            <div className="text-muted-foreground">Prepared by</div>
            <div className="font-semibold">{doc.prepared_by.name}</div>
            <div className="text-muted-foreground">{[doc.prepared_by.email, doc.prepared_by.phone].filter(Boolean).join("  ·  ")}</div>
          </div>
        </header>
      ) : compact ? (
        <header className={`px-8 sm:px-12 pt-10 pb-5 border-b ${centered ? "text-center" : ""}`} data-section-id={cover?.id}>
          <h1 className="text-2xl sm:text-3xl font-bold text-primary" style={c ? { color: c.accent } : undefined}>{doc.title}</h1>
          <div className="mt-1.5 text-sm font-medium text-success" style={c ? { color: c.secondary } : undefined}>{[doc.customer.company, doc.deal_name].filter(Boolean).join(": ")}</div>
        </header>
      ) : (
        <header className="px-8 sm:px-12 pt-8 pb-4 border-b">
          {logo && <div className="mb-3 [&>img]:h-12">{logo}</div>}
          <h1 className="text-xl font-bold text-primary" style={c ? { color: c.accent } : undefined}>{doc.title}</h1>
          <div className="text-[11px] text-muted-foreground mt-1">
            {doc.display_number} · Prepared for {doc.customer.company || "-"} · {fmtDate(doc.date)} · Valid until {fmtDate(doc.valid_until)}
          </div>
        </header>
      )}

      <div className="px-8 sm:px-12 py-8 space-y-7">
        {doc.sections
          .filter((s) => s.type !== "cover")
          .map((s) => {
            // A note is a highlighted box and isn't numbered
            if (s.type === "note") {
              return (
                <section key={s.id} data-section-id={s.id} className="rounded-lg border border-warning/30 bg-warning-soft px-4 py-3" style={c ? { background: c.note_bg, borderColor: c.note_border } : undefined}>
                  <div className="mb-1 text-xs font-bold text-warning" style={c ? { color: c.note_text } : undefined}>{s.title}</div>
                  {s.html && <div className="rte-content" dangerouslySetInnerHTML={{ __html: s.html }} />}
                </section>
              );
            }
            n++;
            return (
              <section key={s.id} data-section-id={s.id}>
                <h2 className="text-base font-bold text-primary mb-2" style={st ? titleStyle(st) : undefined}>
                  {!st || st.numbered_headings ? `${n}. ` : ""}
                  {s.title}
                </h2>
                {s.html && <div className="rte-content" dangerouslySetInnerHTML={{ __html: s.html }} />}
                {s.type === "estimate" && <EstimateTable e={doc.estimate} st={st} />}
                {s.type === "payment" &&
                  (doc.payment?.length ? (
                    <ul className="mt-2 list-disc space-y-1 pl-5">
                      {doc.payment.map((r, i) => (
                        <li key={i}>
                          <strong>{Number(r.percent)}% {r.label.charAt(0).toLowerCase()}{r.label.slice(1)}</strong> - {formatINR(r.amount, true)}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="italic text-muted-foreground mt-2">Payment terms will be agreed with the quotation.</p>
                  ))}
                {s.type === "acceptance" && (
                  <div className="mt-4">
                    <div className="font-semibold">For {doc.customer.company || "the customer"}</div>
                    <div className="grid grid-cols-2 gap-x-8 gap-y-6 mt-6 text-[11px] text-muted-foreground">
                      {["Name", "Title", "Signature", "Date"].map((label) => (
                        <div key={label} className="border-t border-border pt-1">
                          {label}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </section>
            );
          })}
      </div>
      {lh && lh.bottom > 0 && <div aria-hidden style={{ aspectRatio: `210 / ${lh.bottom}`, backgroundImage: `url(${lh.src})`, backgroundSize: "100% auto", backgroundPosition: "bottom", backgroundRepeat: "no-repeat" }} />}
    </article>
  );
}

/**
 * The price table. When every quantity is 1 it is a plain "# / Item / Amount" list; otherwise quantity and unit price show.
 * Under it: subtotal, GST and the total including GST (or the plain indicative total on older proposals).
 */
function EstimateTable({ e, st }: { e: ProposalRender["estimate"]; st?: ProposalStyle | null }) {
  const c = st?.colors;
  // grid: a frame round the table; lines and zebra: lines between rows only (zebra also shades every other row)
  if (!e.lines.length) return <p className="italic text-muted-foreground mt-2">Pricing will be confirmed in the quotation.</p>;
  const simple = e.lines.every((l) => Number(l.quantity) === 1);
  const hasGst = e.gst_rate !== null && e.gst_rate !== undefined;
  const cols = simple ? 3 : 4;
  const foot = (label: string, value: number, cls = "", fill?: string) => (
    <tr className={`border-t border-border font-bold ${cls}`} style={c ? { borderColor: c.border, ...(fill ? { background: fill } : {}) } : undefined}>
      <td colSpan={cols - 1} className={`px-3 py-2 ${simple ? "pl-12 text-left" : "text-right"}`}>{label}</td>
      <td className="px-3 py-2 text-right">{formatINR(value, true)}</td>
    </tr>
  );
  return (
    <table className={`w-full text-xs mt-3 ${st && st.table !== "grid" ? "border-y border-border" : "border border-border"}`} style={c ? { borderColor: c.border } : undefined}>
      <thead>
        <tr className="bg-primary text-white" style={c ? { background: c.table_head_bg, color: c.table_head_text } : undefined}>
          {simple ? (
            <>
              <th className="w-10 px-3 py-2 text-left font-semibold">#</th>
              <th className="px-3 py-2 text-left font-semibold">Item</th>
              <th className="px-3 py-2 text-right font-semibold">Amount (INR)</th>
            </>
          ) : (
            <>
              <th className="text-left px-3 py-2 font-semibold">Description</th>
              <th className="text-right px-3 py-2 font-semibold">Qty</th>
              <th className="text-right px-3 py-2 font-semibold">Unit price</th>
              <th className="text-right px-3 py-2 font-semibold">Amount</th>
            </>
          )}
        </tr>
      </thead>
      <tbody>
        {e.lines.map((l, i) => (
          <tr key={i} className="border-t border-border" style={c ? { borderColor: c.border, ...(st!.table === "zebra" && i % 2 === 1 ? { background: c.zebra } : {}) } : undefined}>
            {simple ? <td className="px-3 py-2 text-muted-foreground">{i + 1}</td> : null}
            <td className="px-3 py-2">{l.description}</td>
            {simple ? null : <td className="px-3 py-2 text-right">{Number(l.quantity)}</td>}
            {simple ? null : <td className="px-3 py-2 text-right">{formatINR(l.unit_price, true)}</td>}
            <td className="px-3 py-2 text-right">{formatINR(Number(l.quantity) * Number(l.unit_price), true)}</td>
          </tr>
        ))}
        {hasGst ? (
          <>
            {foot("Subtotal", e.subtotal ?? e.total, "bg-muted/60")}
            {foot(`GST @ ${Number(e.gst_rate)}%`, e.gst ?? 0, "bg-muted/60")}
            {foot("Total (inclusive of GST)", e.total_incl_gst ?? e.total, "bg-success-soft", c?.total_bg)}
          </>
        ) : (
          foot("Indicative total (excl. GST)", e.total)
        )}
      </tbody>
    </table>
  );
}
